import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BroadCastEvents } from '../../../../common/broad-cast-events.enum';
import { FileExplorerItemViewModel, FileExplorerStructureRespone } from '../../../../models/file-explorer';
import { PatientDocumentFolderDescriptor } from '../../../../models/patient-documents';
import { SecureBaseComponentContext } from '../../../../modules/tcm-common/services/secure-base-component-context.service';
import { FileExplorerApiService } from './file-explorer.api.service';
import { FileItem } from './models/fileItem';
import { Subscription } from 'rxjs/internal/Subscription';

@Injectable()
export class FileExplorerService {
  private patientDocumentRefreshSubscription: Subscription;
  private currentPath: string;
  private originalFiles: FileExplorerItemViewModel[];

  public patientId: number;
  public headerData: any = [];
  public rootItem: FileItem = new FileItem();
  public selected: FileItem;
  public IsListMode = false;
  public hoveredEl: FileItem;
  public hoveredTreeEl: FileItem;

  constructor(private apiService: FileExplorerApiService,
    private toast: ToastrService,
    protected componentContext: SecureBaseComponentContext,
  ) {
    this.selected = new FileItem();

    this.patientDocumentRefreshSubscription = this.componentContext.broadcastService.getEvents(BroadCastEvents.PatientDocumentChanged).subscribe((event: any) => {
      if (event && event.args) {
        const file = event.args as FileExplorerItemViewModel;
        let fileItem: FileItem = new FileItem();
        fileItem.id = file.id;
        fileItem.name = file.name;
        fileItem.type = 'file';
        fileItem.path = this.selected.path;
        fileItem.parent = this.selected;
        fileItem.createdDate = file.createdDate;
        fileItem.cssClass = this.getCssClassByName(file.name);

        this.selected.children.push(fileItem);
      }
    });
  }

  public setupRoot(patientId) {
    this.patientId = patientId;
    let fileItem = new FileItem();
    fileItem.name = "root";
    fileItem.type = "dir";
    fileItem.path = "";
    fileItem.cssClass = "folder";

    this.rootItem = fileItem;
    this.setSelected(this.rootItem);
    this.getStructure(fileItem, patientId);
  }

  public setSelected(node: FileItem) {
    if (!node) {
      return;
    }
    if (node.type == 'file') {
      return this.handleFileDblClick(node);
    }
    this.currentPath = node.path;
    this.selected = node;
    node.isOpen = true;
    this.setHeader(node);
  }

  public toggleView(){
    this.IsListMode=!this.IsListMode;
  }

  public handleFileDblClick(node: FileItem) {
    this.download(node);
  }

  public toggleExpand(node) {
    node.isOpen = !node.isOpen;
    if (node != this.selected)
      this.setSelected(node);
  }

  public setHoveredTreeItem(fileItem: FileItem) {
    this.hoveredTreeEl = fileItem;
  }

  public clearHoveredTreeItem() {
    this.hoveredTreeEl = null;
  }

  public setHoveredItem(fileItem: FileItem) {
    this.hoveredEl = fileItem;
  }

  public clearHoveredItem() {
    this.hoveredEl = null;
  }

  public async renameItemAsync(item: FileItem, newName: string) {
    if (newName) {
      let oldPath: string = item.path;
      let oldName: string = item.name;

      if (item.type == "file") {
        let nameParts = oldName.split(".");
        item.name = newName + "." + nameParts[nameParts.length - 1];
        item.path = item.parent ? item.parent.path : "" + '\\' + item.name;
        await this.apiService.renameAsync(item.id, item.name);
      }
      if (item.type == "dir") {
        item.name = newName;
        item.path = item.parent ? item.parent.path + '\\' + item.name : '\\' + item.name;
        await this.apiService.renameFolderAsync(this.patientId, oldPath, item.path);
        this.updateFolderStructure();
      }

      this.toast.success(this.componentContext.translationService.translate(x => x.Screens.FileExplorer.Messages.RenameSuccessfull));
    }
  }

  public async download(item: FileItem) {
    try {
      if (item.type == "file") {        
        const response = await this.apiService.downloadAndDisplayFileAsync(item.id);

        response.save();
      }
    }
    catch (e) {

    }
  }

  public async delete(item: FileItem) {
    const isApproved = await this.componentContext.modalService.showConfirmationModalAsync(
      x => x.Messages.MessageHeadersAndActions.Warning,
      x => x.Screens.FileExplorer.Messages.AreYouSureYouWantToDeleteSelectedItem);

    if (isApproved) {
      if (item.type == "file") {
        await this.apiService.deleteFileAsync(item.id);
      }
      if (item.type == "dir") {
        await this.apiService.deleteFilesAsync(this.patientId, item.path);
      }
      this.toast.success(this.componentContext.translationService.translate(x => x.Screens.FileExplorer.Messages.FilesDeletedSuccessfully));

      this.selected.children.splice(this.selected.children.indexOf(item), 1);
      this.updateFolderStructure();
    }
  }

  public async addFolderAsync(folderName: string) {
    if (folderName) {
      let fileItem: FileItem = new FileItem();
      fileItem.name = folderName;
      fileItem.type = 'dir';
      fileItem.path = this.selected.path + "\\" + folderName;
      fileItem.parent = this.selected;
      fileItem.children = [];
      fileItem.cssClass = "folder";

      this.selected.children.push(fileItem);
      await this.updateFolderStructure();
    }
  }

  public move(item: FileItem) {
    this.apiService.moveFileAsync(item.id, item.path);
  }

  private getCssClassByName(name: string): string {
    if (name.match(/.(jpg|jpeg|png|gif)$/i)) {
      return "file-image";
    }
    if (name.match(/.(doc|docs)$/i)) {
      return "file-word";
    }
    if (name.match(/.(xlsx|csv|xls)$/i)) {
      return "file-excel";
    }
    if (name.match(/.(pdf)$/i)) {
      return "file-pdf";
    }
    if (name.match(/.(txt)$/i)) {
      return "file-txt";
    }

    return "file";
  }

  private setHeader(node: FileItem) {
    this.headerData = [];
    let tempHeaders = [];
    let currentNode = node;
    let nameStr = "";

    while (currentNode) {
      let name = (currentNode.parent ? currentNode.name : "Root");
      if (name.length > 20) {
        name = name.substring(0, 20) + "...";
      }
      tempHeaders.push({ name: name, isActive: (node == currentNode), node: currentNode })
      currentNode = currentNode.parent;
      nameStr += name;
    }

    if (nameStr.length > 60) {
      tempHeaders = [];
      tempHeaders.push({ name: node.name.length > 20 ? node.name.substring(0, 20) + "..." : node.name, isActive: true, node: node });
      tempHeaders.push({ name: "...", isActive: false, node: null });
      tempHeaders.push({ name: this.rootItem.name, isActive: false, node: this.rootItem });
    }

    while (tempHeaders.length) this.headerData.push(tempHeaders.pop());
  }

  private async getStructure(node: FileItem, patientId: number) {
    let data: FileExplorerStructureRespone = await this.apiService.getStructureAsync(patientId);
    this.originalFiles = data.files;
    this.prepareStructure(data.structure, node);
  }

  private prepareStructure(structure: PatientDocumentFolderDescriptor[], node: FileItem) {
    let result: FileItem[] = [];
    if (structure && structure.length > 0) {
      structure.forEach(x => {
        let fileItem: FileItem = new FileItem();
        fileItem.name = x.name;
        fileItem.type = 'dir';
        fileItem.path = node.path + '\\' + x.name;
        fileItem.parent = node;
        fileItem.cssClass = "folder";

        this.prepareStructure(x.children, fileItem);

        result.push(fileItem);
      });
    }
    node.children = result;
    this.getFiles(node);
  }

  private async getFiles(node: FileItem) {
    var nodeFiles = this.originalFiles.filter(x => ((x.path == null && node.path == "") || (x.path != null && x.path.toLowerCase() == node.path.toLowerCase())))
    if (nodeFiles && nodeFiles.length > 0) {
      nodeFiles.forEach(x => {
        let fileItem: FileItem = new FileItem();
        fileItem.id = x.id;
        fileItem.name = x.name;
        fileItem.type = 'file';
        fileItem.path = node.path;
        fileItem.parent = node;
        fileItem.createdDate = x.createdDate;
        fileItem.cssClass = this.getCssClassByName(x.name);

        node.children.push(fileItem);
      });
    }
  }

  private updateFolderStructure() {
    let newStructure: PatientDocumentFolderDescriptor[] = this.getNewStructure(this.rootItem.children);
    return this.apiService.updateStructureAsync(newStructure, this.patientId);
  }

  private getNewStructure(children: FileItem[]): PatientDocumentFolderDescriptor[] {
    if (!children) {
      return [];
    }
    let newStructure: PatientDocumentFolderDescriptor[] = [];
    children.forEach(x => {
      if (x.type != 'dir') {
        return;
      }
      let newItem: PatientDocumentFolderDescriptor = {} as PatientDocumentFolderDescriptor;
      newItem.name = x.name;
      newItem.children = this.getNewStructure(x.children);

      newStructure.push(newItem);
    });

    return newStructure;
  }
}
